import {
  CREATOR_ORDER_V2_ERROR_CODE,
  isCreatorOrderV2ApiError
} from '@/apis/creatorOrderV2/errors';
import type { CreatorCollaborationPackage } from './model';

export type CreatorQuoteBindingConflict = {
  channelKey: string;
  ownerEmail?: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

/** Join's 47017 payload is distinct from Final Link validation failures. */
export function getCreatorQuoteBindingFailure(error: unknown): {
  channels: CreatorQuoteBindingConflict[];
} | null {
  if (!isRecord(error)) return null;
  // HTTP failures retain responseData/payload in axiosInstance's wrapped Error.
  const response = isRecord(error.responseData) ? error.responseData : error;
  const payload = isCreatorOrderV2ApiError(error)
    ? error.payload
    : isRecord(response.payload)
      ? response.payload
      : isRecord(error.payload)
        ? error.payload
        : undefined;
  if (
    response.code !== CREATOR_ORDER_V2_ERROR_CODE.CHANNEL_BOUND_BY_OTHER &&
    payload?.reason !== 'CHANNEL_BOUND_BY_OTHER'
  )
    return null;
  const channels = Array.isArray(payload?.channels)
    ? payload.channels.flatMap((channel: unknown) => {
        if (
          !isRecord(channel) ||
          typeof channel.channelKey !== 'string' ||
          !channel.channelKey.trim()
        )
          return [];
        const email = isRecord(channel.user) ? channel.user.email : undefined;
        return [
          {
            channelKey: channel.channelKey,
            ownerEmail:
              typeof email === 'string' && email.trim() ? email : undefined
          }
        ];
      })
    : [];
  return { channels };
}

/** Keep server failures local to this quote without changing the order DTO. */
export function withCreatorQuoteBindingConflicts(
  collaborationPackage: CreatorCollaborationPackage,
  channels: CreatorQuoteBindingConflict[]
): CreatorCollaborationPackage {
  if (!channels.length) return collaborationPackage;
  const conflicts = new Map(
    channels.map((channel) => [channel.channelKey, channel])
  );
  return {
    ...collaborationPackage,
    placements: collaborationPackage.placements.map((placement) => {
      const conflict = placement.channelKey
        ? conflicts.get(placement.channelKey)
        : undefined;
      return conflict
        ? {
            ...placement,
            raw: {
              ...placement.raw,
              accountOwnershipConflict: true,
              boundOwnerEmail: conflict.ownerEmail,
              linkedAccountMaskedEmail: undefined
            }
          }
        : placement;
    })
  };
}
